import { useState } from 'react';
import { Grid, Typography, Box, IconButton } from '@mui/material';
import { toast } from 'react-toastify';
import moment from 'moment';

import {
  FHAntdDateTimePicker,
  FHAutocomplete,
  FHButton,
  FHConfirmDialog,
  FHInputField,
} from '../../components/base';
import { useServiceCodeOptions } from '../hooks';
import { FORTA_BLUE, FORTA_WHITE } from '../../utils/colors';
import { Themes } from '../../constants';
import TrashIcon from '../../components/svgs/TrashIcon';
import EditOutlinedIcon from '../../components/svgs/EditOutlinedIcon';

import type { HTMLAttributes } from 'react';
import type { Moment } from 'moment';
import type { IPayorContract, IServiceCode } from '../../utils/types';

const PayorContractRow = ({
  contract,
  isAdding,
  isEditing,
  isDisabled,
  onEdit,
  onCancel,
  onSave,
  onDelete,
}: {
  contract: IPayorContract;
  isAdding: boolean;
  isEditing: boolean;
  isDisabled: boolean;
  onEdit: () => void;
  onCancel: () => void;
  onSave: (editableContract: IPayorContract) => void;
  onDelete: (editableContract: IPayorContract) => void;
}) => {
  const procedureCodes = useServiceCodeOptions();
  const [editableContract, setEditableContract] = useState({ ...contract });
  const [showConfirmDialog, setShowConfirmDialog] = useState<boolean>(false);

  const handleFieldChange = (field: string, value: any) => {
    setEditableContract((prevContract: any) => ({ ...prevContract, [field]: value }));
  };

  const validate = () => {
    const { rate_per_unit } = editableContract;
    if (!rate_per_unit || isNaN(rate_per_unit)) {
      toast.error('Rate per unit is required and should be a number', {
        toastId: 'rate-per-unit-error',
      });
      return false;
    }
    if (!editableContract.service_code_id) {
      toast.error('Service code is required', { toastId: 'service-code-error' });
      return false;
    }
    if (!editableContract.date_start) {
      toast.error('Start date is required', { toastId: 'start-date-error' });
      return false;
    }
    return true;
  };

  const handleSave = () => {
    if (validate()) {
      onSave(editableContract);
    }
  };

  const handleCancel = () => {
    setEditableContract({ ...contract });
    onCancel();
  };

  return (
    <Box>
      <Grid container p={1} spacing={1} direction="row" alignItems="center">
        <Grid item xs={3}>
          {isAdding ? (
            <FHAutocomplete
              data-testid="serviceCodeDrowdownId"
              sx={{ flexGrow: 1, minWidth: 275, boxShadow: 'none' }}
              inputHeight={40}
              colorTheme={Themes.Primary}
              placeholder="Select service code"
              clearIcon={false}
              value={procedureCodes.find((x) => x.id === editableContract.service_code_id) || null}
              isOptionEqualToValue={(option: any, value: any) => option.id === value.id}
              renderOption={(props: HTMLAttributes<HTMLLIElement>, option: IServiceCode) => {
                return (
                  <li {...props} key={option.id} data-testid={`procedureCode${option.code}`}>
                    {`${option.label}`}
                  </li>
                );
              }}
              options={procedureCodes}
              onChange={(e: any, value: any) => handleFieldChange('service_code_id', value?.id)}
            />
          ) : (
            <Typography textAlign={'left'}>
              {procedureCodes.find((x) => x.id === contract.service_code_id)?.label}
            </Typography>
          )}
        </Grid>
        <Grid item xs={2}>
          {isAdding || isEditing ? (
            <FHInputField
              inputProps={{ 'data-testid': 'ratePerUnit' }}
              fullWidth
              inputHeight={40}
              sx={{ width: '100%', boxShadow: 'none' }}
              value={editableContract.rate_per_unit}
              onChange={(e: any) => handleFieldChange('rate_per_unit', e.target.value)}
            />
          ) : (
            <Typography textAlign={'center'}>{contract.rate_per_unit}</Typography>
          )}
        </Grid>
        <Grid item xs={2}>
          {isAdding || isEditing ? (
            <FHAntdDateTimePicker
              data-testid="contractStartDate"
              format="MM/DD/YYYY"
              inputHeight={40}
              sx={{ width: '100%', boxShadow: 'none' }}
              allowClear={false}
              value={editableContract.date_start ? moment(editableContract.date_start) : null}
              onSelect={(value: Moment) =>
                handleFieldChange('date_start', value.format('YYYY-MM-DD'))
              }
              onChange={(value: Moment) =>
                handleFieldChange('date_start', value.format('YYYY-MM-DD'))
              }
            />
          ) : (
            <Typography textAlign={'center'}>
              {moment(contract.date_start).format('MM/DD/YYYY')}
            </Typography>
          )}
        </Grid>
        <Grid item xs={2}>
          {isAdding || isEditing ? (
            <FHAntdDateTimePicker
              data-testid="contractEndDate"
              format="MM/DD/YYYY"
              inputHeight={40}
              sx={{ width: '100%', boxShadow: 'none' }}
              allowClear={true}
              value={editableContract.date_end ? moment(editableContract.date_end) : null}
              onSelect={(value: Moment) =>
                handleFieldChange('date_end', value.format('YYYY-MM-DD'))
              }
              onChange={(value: Moment | null) =>
                handleFieldChange('date_end', value ? value.format('YYYY-MM-DD') : null)
              }
            />
          ) : (
            <Typography textAlign={'center'}>
              {contract.date_end ? moment(contract.date_end).format('MM/DD/YYYY') : 'N/A'}
            </Typography>
          )}
        </Grid>
        <Grid item xs={1}>
          {isAdding || isEditing ? (
            <FHButton data-testid="saveContractButton" disabled={isDisabled} onClick={handleSave}>
              Save
            </FHButton>
          ) : (
            <IconButton
              data-testid="editContractButton"
              sx={{
                width: 48,
                height: 30,
                border: `2px solid ${FORTA_BLUE.primary}`,
                borderRadius: '20px',
                backgroundColor: isEditing ? FORTA_BLUE.primary : 'transparent',
                mr: 3,
                '&:hover': {
                  backgroundColor: isEditing ? FORTA_BLUE.primary : 'transparent',
                },
              }}
              onClick={onEdit}
            >
              <EditOutlinedIcon color={isEditing ? FORTA_WHITE : FORTA_BLUE.primary} width={18} />
            </IconButton>
          )}
        </Grid>
        <Grid item xs={1}>
          {isAdding || isEditing ? (
            <FHButton data-testid="cancelContractButton" onClick={handleCancel}>
              Cancel
            </FHButton>
          ) : (
            <IconButton
              data-testid="deleteContractButton"
              sx={{
                width: 48,
                height: 30,
                border: `2px solid ${FORTA_BLUE.primary}`,
                borderRadius: '20px',
                backgroundColor: isEditing ? FORTA_BLUE.primary : 'transparent',
                mr: 3,

                '&:hover': {
                  backgroundColor: isEditing ? FORTA_BLUE.primary : 'transparent',
                },
              }}
              disabled={isDisabled}
              onClick={() => setShowConfirmDialog(true)}
            >
              <TrashIcon color={isEditing ? FORTA_WHITE : FORTA_BLUE.primary} width={20} />
            </IconButton>
          )}
        </Grid>
        <FHConfirmDialog
          data-testid="contractConfirmDialog"
          open={showConfirmDialog}
          colorTheme={Themes.Primary}
          title={'Delete Contract'}
          content={'Are you sure you want to delete this contract? This action cannot be undone.'}
          submittext={'Yes, Delete'}
          canceltext={'Cancel'}
          onClose={() => setShowConfirmDialog(false)}
          onSubmit={() => onDelete(editableContract)}
        />
      </Grid>
    </Box>
  );
};

export default PayorContractRow;
